import AccessTimeIcon from '@mui/icons-material/AccessTime';
import {Typography, useTheme} from '@mui/material';
import Box from '@mui/material/Box';
import {CursorBasedPaginationResponse} from 'api/common';
import {BaseCard} from 'components/card';
import AppBaseCardHeader from 'components/card/AppBaseCardHeader';
import {AppMenu} from 'components/menus';
import {Edit} from 'iconsax-react';
import {formatSecondsToTime, TimeTrackerListingRequest, TimeTrackerModel, TimeTrackerStatus} from 'models';
import {useEffect, useMemo, useState} from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {sessionSelector} from 'store/auth/sessionSlice';
import TimeTrackerAddUpdateManulEntryDialog from '../TimeTrackerAddUpdateManulEntryDialog';
import {TimerCardSkeleton} from './TimerCardSkeleton';

interface TimeTrackerHistoryProps {
    height?: string;
    itemDataId?: string;
    handleCloseAddUpdateDialog: () => void;
    openAddUpdateDialog: boolean;
    getAllTimeTrackerEntities: (value: TimeTrackerListingRequest) => void;
    getAllTimeTrackerEntitiesResponse: CursorBasedPaginationResponse<TimeTrackerModel> | undefined;
    getAllTimeTrackerEntitiesLoading: boolean;
    afterCreateUpdateHandler: (item: TimeTrackerModel) => void;
    handleOpenAddUpdateDialog: () => void;
}

export const TimeTrackerHistory: React.FC<TimeTrackerHistoryProps> = ({
    height = 'calc(100vh - 230px)',
    handleCloseAddUpdateDialog,
    openAddUpdateDialog,
    itemDataId,
    getAllTimeTrackerEntities,
    getAllTimeTrackerEntitiesResponse,
    getAllTimeTrackerEntitiesLoading,
    afterCreateUpdateHandler,
    handleOpenAddUpdateDialog,
}) => {
    const {tenantId, userId} = useSelector(sessionSelector);

    const filter = useMemo(() => {
        const requiredFilter: TimeTrackerListingRequest = {
            where: {
                status: {
                    or: [{eq: TimeTrackerStatus.Completed}, {eq: TimeTrackerStatus.Manual}],
                },
            },
        };
        if (itemDataId && requiredFilter.where) {
            requiredFilter.where.itemDataId = {eq: itemDataId};
        } else if (requiredFilter.where) {
            requiredFilter.where.userInfoId = {eq: userId};
        }
        return requiredFilter;
    }, [userId, itemDataId]);

    useEffect(() => {
        getAllTimeTrackerEntities({
            ...filter,
        });
    }, [tenantId]);

    const [selectedEditItem, setSelectedEditItem] = useState<string>();

    const handleNext = () => {
        getAllTimeTrackerEntities({...filter, after: getAllTimeTrackerEntitiesResponse?.pageInfo.endCursor});
    };
    const navigate = useNavigate();
    const theme = useTheme();
    useEffect(() => {
        if (selectedEditItem) {
            handleOpenAddUpdateDialog();
        }
    }, [selectedEditItem]);
    const handleCloseDialog = () => {
        handleCloseAddUpdateDialog();
        if (selectedEditItem) {
            setSelectedEditItem(undefined);
        }
    };

    return (
        <>
            <TimeTrackerAddUpdateManulEntryDialog
                timeTrackerId={selectedEditItem}
                afterUpdateAndCreateHandler={afterCreateUpdateHandler}
                open={openAddUpdateDialog}
                onCloseDialog={handleCloseDialog}
            />
            {getAllTimeTrackerEntitiesLoading && !getAllTimeTrackerEntitiesResponse && (
                <>
                    <TimerCardSkeleton />
                    <TimerCardSkeleton />
                    <TimerCardSkeleton />
                    <TimerCardSkeleton />
                    <TimerCardSkeleton />
                </>
            )}
            {getAllTimeTrackerEntitiesResponse && (
                <InfiniteScroll
                    height={height}
                    style={{
                        scrollbarWidth: 'thin',
                        scrollbarColor: 'rgb(203, 211, 222) transparent',
                    }}
                    scrollableTarget="scrollableDiv"
                    dataLength={getAllTimeTrackerEntitiesResponse?.nodes.length ?? 0}
                    next={handleNext}
                    hasMore={getAllTimeTrackerEntitiesResponse?.pageInfo.hasNextPage ?? false}
                    loader="Loading More..."
                >
                    {getAllTimeTrackerEntitiesResponse?.nodes.map(item => {
                        return (
                            <BaseCard
                                key={item.id}
                                icon={<AccessTimeIcon color="primary" style={{fontSize: '30px'}} />}
                                menus={
                                    <AppMenu
                                        menuItems={[
                                            {
                                                label: 'Edit',
                                                icon: (
                                                    <Box mt={1}>
                                                        <Edit
                                                            color={theme.palette.primary.main}
                                                            variant="Outline"
                                                            size={'16px'}
                                                        />
                                                    </Box>
                                                ),
                                                onClick: () => {
                                                    setSelectedEditItem(item.id);
                                                },
                                            },
                                        ]}
                                    />
                                }
                                headerData={
                                    <AppBaseCardHeader
                                        dateTime={item.createdDate}
                                        chipProps={{
                                            children: (
                                                <>{item.itemDataURL ? `Ticket #${item.ticketNo}` : item.status}</>
                                            ),
                                            onClick: () => {
                                                if (item.itemDataURL) {
                                                    navigate(item.itemDataURL);
                                                }
                                            },
                                        }}
                                        title={
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    Spent Time:
                                                </Typography>
                                                <Typography variant="subtitle1" fontWeight="bold">
                                                    {formatSecondsToTime(item.durationInSeconds)}
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                }
                                footerData={<Typography variant="body1">{item.title}</Typography>}
                            />
                        );
                    })}
                </InfiniteScroll>
            )}
        </>
    );
};
export default TimeTrackerHistory;
